import { TabPanels as ChakraTabPanels, Tabs as ChakraTabs } from '@chakra-ui/react'
import * as React from 'react'

import { CarouselNavigation, TFooterProps } from './CarouselNavigation'
import { CarouselTabContent } from './CarouselTabContent'

type TNavigation = OmitSafe<
  TFooterProps,
  'selectedIndex' | 'setTabIndex' | 'tabsCount' | 'children'
>

type TTabContentProps = PropsOf<typeof CarouselTabContent>

type TProps = NoChildren & {
  /**
   * whether is the carousel rendered on mobile device
   */
  isMobile: boolean
  /**
   * data to be rendered
   */
  items: RoA<TTabContentProps['item']>
  /**
   * navigation props
   */
  navigationProps?: TNavigation
  /**
   * any image element such as next/image, HTML img element or other, if no such element is provided, the chakra image is used with the item's imageUrl
   */
  customImageElement?: TTabContentProps['customImageElement']
}

/**
 * Carousel component - renders the items as tabs with navigation.
 */
export const Carousel: React.FC<TProps> = ({
  items,
  isMobile,
  navigationProps,
  customImageElement,
  ...chakraProps
}) => {
  const [tabIndex, setTabIndex] = React.useState(0)

  const selectTab = React.useCallback((index: number) => {
    setTabIndex(index)
  }, [])

  return (
    <ChakraTabs
      index={tabIndex}
      onChange={selectTab}
      display={`grid`}
      gridTemplateRows={`1fr auto`}
      height={`100%`}
      {...chakraProps}
    >
      {/* Content of the tab */}
      <ChakraTabPanels overflow={`auto`}>
        {items.map((item, index) => (
          <CarouselTabContent
            key={getKey(index, item.text)}
            item={item}
            isMobile={isMobile}
            customImageElement={customImageElement}
          />
        ))}
      </ChakraTabPanels>

      {/* Navigation */}
      {navigationProps && (
        <CarouselNavigation
          tabsCount={items.length}
          selectedIndex={tabIndex}
          setTabIndex={setTabIndex}
          {...navigationProps}
        />
      )}
    </ChakraTabs>
  )
}

//* HELPERS

/**
 * @returns unique key for the item
 */
export const getKey = (index: number, text: string) => {
  return index + text
}
